/** Minimal IndexedDB key → bytes store for the sql.js database file. No dependencies. */
import type { ByteStore } from "./sqljsDb";

const DB_NAME = "npac-field";
const STORE = "files";

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

export class IdbByteStore implements ByteStore {
  private dbp: Promise<IDBDatabase> | null = null;

  constructor(private readonly idb: IDBFactory = indexedDB, private readonly dbName = DB_NAME) {}

  private open(): Promise<IDBDatabase> {
    if (!this.dbp) {
      this.dbp = new Promise((resolve, reject) => {
        const r = this.idb.open(this.dbName, 1);
        r.onupgradeneeded = () => {
          if (!r.result.objectStoreNames.contains(STORE)) r.result.createObjectStore(STORE);
        };
        r.onsuccess = () => {
          const db = r.result;
          // another tab upgraded the schema: close so it is not blocked
          db.onversionchange = () => db.close();
          resolve(db);
        };
        r.onerror = () => reject(r.error);
        r.onblocked = () => reject(new Error("IndexedDB is blocked by another tab"));
      });
      this.dbp.catch(() => {
        this.dbp = null;
      });
    }
    return this.dbp;
  }

  async load(key: string): Promise<Uint8Array | null> {
    const db = await this.open();
    const tx = db.transaction(STORE, "readonly");
    const v = await req(tx.objectStore(STORE).get(key));
    if (v == null) return null;
    return v instanceof Uint8Array ? v : new Uint8Array(v as ArrayBuffer);
  }

  async save(key: string, bytes: Uint8Array): Promise<void> {
    const db = await this.open();
    // "strict": resolve only once the data is flushed to disk (Chrome defaults to relaxed)
    const tx = db.transaction(STORE, "readwrite", { durability: "strict" } as IDBTransactionOptions);
    tx.objectStore(STORE).put(bytes, key);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error ?? new Error("IndexedDB write aborted"));
    });
  }
}
